
import React, { useEffect, useMemo, useState } from "react";

import {
  AlertCircle,
  Building2,
  CalendarDays,
  Download,
  ExternalLink,
  FileText,
  GraduationCap,
  Info,
  Loader2,
  RefreshCw,
  School,
  Users,
} from "lucide-react";

import { api, resolveMediaUrl } from "../lib/api";

/* ==========================================
   HELPER FUNCTIONS
========================================== */

function normalizeCategory(category = "") {
  const value = String(category).toLowerCase().trim();

  if (value.includes("class xii")) {
    return "resultClassXII";
  }

  if (value.includes("class x")) {
    return "resultClassX";
  }

  if (value.includes("general")) {
    return "generalInformation";
  }

  if (value.includes("staff")) {
    return "staffDetails";
  }

  if (value.includes("infrastructure")) {
    return "schoolInfrastructure";
  }

  const documentKeywords = [
    "document",
    "certificate",
    "affiliation",
    "trust",
    "pta",
    "safety",
    "property",
    "calendar",
    "fee",
    "admission",
  ];

  if (
    documentKeywords.some((keyword) =>
      value.includes(keyword)
    )
  ) {
    return "documentsInformation";
  }

  return "otherInformation";
}

function sortByPosition(items = []) {
  return [...items].sort(
    (a, b) =>
      Number(a.position || 0) -
      Number(b.position || 0)
  );
}

function getApiRecords(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.results)) {
    return response.results;
  }

  return [];
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getDocumentUrl(item) {
  const possibleUrls = [
    item?.document?.url,
    item?.document?.path,
    item?.document?.file,
    item?.documentUrl,
    item?.document_url,
    item?.pdf?.url,
    item?.pdf?.path,
    item?.pdfUrl,
    item?.file?.url,
    item?.file?.path,
    item?.fileUrl,
    item?.document,
    item?.pdf,
    item?.file,
  ];

  const result = possibleUrls.find(
    (value) =>
      typeof value === "string" &&
      value.trim() !== ""
  );

  return result?.trim() || "";
}

function getDocumentName(item) {
  const possibleNames = [
    item?.document?.name,
    item?.documentName,
    item?.document_name,
    item?.pdf?.name,
    item?.file?.name,
  ];

  const providedName = possibleNames.find(
    (value) =>
      typeof value === "string" &&
      value.trim()
  );

  if (providedName) {
    return providedName.trim();
  }

  const url = getDocumentUrl(item);

  if (!url) return "Document.pdf";

  try {
    const filename = url
      .split("?")[0]
      .split("#")[0]
      .split("/")
      .pop();

    return (
      decodeURIComponent(filename || "") ||
      "Document.pdf"
    );
  } catch {
    return "Document.pdf";
  }
}

function getFinalDocumentUrl(item) {
  const url = getDocumentUrl(item);

  if (!url) return "";

  return resolveMediaUrl(url);
}

async function downloadDocument(
  url,
  filename = "Document.pdf"
) {
  if (!url) return;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error("Unable to download document.");
    }

    const blob = await response.blob();

    const objectUrl =
      window.URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 1000);
  } catch {
    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  }
}

/* ==========================================
   SHARED SECTION CARD
========================================== */

function SectionCard({
  title,
  icon: Icon,
  count,
  children,
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-red-100 bg-white shadow-[0_8px_28px_rgba(80,30,30,0.05)]">

      {/* Section Header */}
      <div className="relative flex flex-wrap items-center justify-between gap-3 border-b border-red-100 bg-gradient-to-r from-red-50/70 via-white to-amber-50/60 px-4 py-3.5 sm:px-6">

        <div className="flex items-center gap-3">

          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-800 text-white">
            <Icon size={19} strokeWidth={1.8} />
          </div>

          <h2 className="font-serif text-lg font-medium text-slate-900 sm:text-xl">
            {title}
          </h2>
        </div>

        <span className="rounded-full border border-red-100 bg-white px-3 py-1 text-xs font-normal text-red-800">
          {count} {count === 1 ? "Record" : "Records"}
        </span>
      </div>

      {children}
    </section>
  );
}

/* ==========================================
   TABLE COMPONENTS
========================================== */

function TableHeading({
  children,
  className = "",
}) {
  return (
    <th
      scope="col"
      className={`border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-left text-xs font-medium tracking-wide text-slate-600 sm:px-5 ${className}`}
    >
      {children}
    </th>
  );
}

function NumberCell({ value }) {
  return (
    <td className="px-4 py-3.5 sm:px-5">
      <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-red-50 px-2 text-xs font-normal text-red-700">
        {value}
      </span>
    </td>
  );
}

/* ==========================================
   GENERAL INFORMATION
========================================== */

function GeneralInformationSection({ items }) {
  if (!items?.length) return null;

  return (
    <SectionCard
      title="General Information"
      icon={Info}
      count={items.length}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[650px] border-collapse">
          <thead>
            <tr>
              <TableHeading className="w-20">
                S. No.
              </TableHeading>

              <TableHeading>
                Information
              </TableHeading>

              <TableHeading>
                Details
              </TableHeading>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {items.map((item, index) => (
              <tr
                key={
                  item._id ||
                  `${item.title}-${index}`
                }
                className="transition-colors hover:bg-red-50/30"
              >
                <NumberCell value={index + 1} />

                <td className="px-4 py-3.5 text-sm font-medium text-slate-800 sm:px-5">
                  {item.title || "—"}
                </td>

                <td className="px-4 py-3.5 text-sm font-normal leading-6 text-slate-600 sm:px-5">
                  {item.detail ||
                    item.description ||
                    item.value ||
                    "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/* ==========================================
   DOCUMENT INFORMATION
========================================== */

function DocumentSection({
  title,
  icon,
  items,
}) {
  if (!items?.length) return null;

  return (
    <SectionCard
      title={title}
      icon={icon}
      count={items.length}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[750px] border-collapse">

          <thead>
            <tr>
              <TableHeading className="w-20">
                S. No.
              </TableHeading>

              <TableHeading>
                Information / Document
              </TableHeading>

              <TableHeading className="w-[270px] text-right">
                Action
              </TableHeading>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {items.map((item, index) => {
              const pdfUrl =
                getFinalDocumentUrl(item);

              const documentName =
                getDocumentName(item);

              return (
                <tr
                  key={
                    item._id ||
                    `${item.title}-${index}`
                  }
                  className="transition-colors hover:bg-red-50/30"
                >
                  <NumberCell
                    value={index + 1}
                  />

                  {/* Document Details */}
                  <td className="px-4 py-3.5 sm:px-5">

                    <div className="flex items-center gap-3">

                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
                        <FileText size={19} />
                      </div>

                      <div className="min-w-0">

                        <p className="text-sm font-medium text-slate-800">
                          {item.title || "—"}
                        </p>

                        {pdfUrl ? (
                          <a
                            href={pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={documentName}
                            className="mt-1 block max-w-[350px] truncate text-xs font-normal text-slate-500 transition hover:text-red-700 hover:underline"
                          >
                            {documentName}
                          </a>
                        ) : (
                          <p className="mt-1 text-xs font-normal text-red-500">
                            Document not uploaded
                          </p>
                        )}

                        {!pdfUrl &&
                          item.detail && (
                            <p className="mt-1 text-xs font-normal text-slate-500">
                              {item.detail}
                            </p>
                          )}

                      </div>
                    </div>
                  </td>

                  {/* Action Buttons */}
                  <td className="px-4 py-3.5 sm:px-5">
                    {pdfUrl ? (
                      <div className="flex flex-wrap justify-end gap-2">

                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-800 transition hover:bg-red-50"
                        >
                          <ExternalLink size={14} />
                          View PDF
                        </a>

                        <button
                          type="button"
                          onClick={() =>
                            downloadDocument(
                              pdfUrl,
                              documentName
                            )
                          }
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-800 px-3 py-2 text-xs font-medium text-white transition hover:bg-red-900"
                        >
                          <Download size={14} />
                          Download
                        </button>

                      </div>
                    ) : (
                      <div className="text-right">
                        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-normal text-slate-500">
                          Not Available
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/* ==========================================
   CLASS X AND XII RESULTS
========================================== */

function ResultSection({ title, items }) {
  if (!items?.length) return null;

  return (
    <SectionCard
      title={title}
      icon={GraduationCap}
      count={items.length}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] border-collapse">

          <thead>
            <tr>
              <TableHeading>
                S. No.
              </TableHeading>

              <TableHeading>
                Academic Year
              </TableHeading>

              <TableHeading className="text-center">
                Registered Students
              </TableHeading>

              <TableHeading className="text-center">
                Students Passed
              </TableHeading>

              <TableHeading className="text-center">
                Pass Percentage
              </TableHeading>

              <TableHeading>
                Remarks
              </TableHeading>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {items.map((item, index) => {
              const registered = Number(
                item.registeredStudents || 0
              );

              const passed = Number(
                item.passedStudents || 0
              );

              const calculatedPercentage =
                registered > 0
                  ? (
                      (passed / registered) *
                      100
                    ).toFixed(2)
                  : "";

              const percentage =
                item.passPercentage !== undefined &&
                item.passPercentage !== null &&
                item.passPercentage !== ""
                  ? item.passPercentage
                  : calculatedPercentage;

              return (
                <tr
                  key={
                    item._id ||
                    `${item.academicYear}-${index}`
                  }
                  className="transition-colors hover:bg-amber-50/30"
                >
                  <NumberCell
                    value={index + 1}
                  />

                  <td className="px-4 py-3.5 text-sm font-medium text-slate-800 sm:px-5">
                    {item.academicYear ||
                      item.title ||
                      "—"}
                  </td>

                  <td className="px-4 py-3.5 text-center text-sm font-normal text-slate-600 sm:px-5">
                    {item.registeredStudents ?? "—"}
                  </td>

                  <td className="px-4 py-3.5 text-center text-sm font-normal text-slate-600 sm:px-5">
                    {item.passedStudents ?? "—"}
                  </td>

                  <td className="px-4 py-3.5 text-center sm:px-5">
                    {percentage !== "" ? (
                      <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
                        {String(percentage).replace(
                          /%$/,
                          ""
                        )}
                        %
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td className="px-4 py-3.5 text-sm font-normal text-slate-600 sm:px-5">
                    {item.remarks || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/* ==========================================
   MAIN COMPONENT
========================================== */

export default function MandatoryDisclosure() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;

    async function loadDisclosures() {
      setLoading(true);
      setError("");

      try {
        const response =
          await api.activeMandatoryDisclosures();

        if (!mounted) return;

        const items = getApiRecords(response).filter(
          (item) => item.isActive !== false
        );

        setRecords(items);
      } catch (err) {
        if (!mounted) return;

        setError(
          err?.message ||
            "Unable to load mandatory disclosures."
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadDisclosures();

    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const groupedRecords = useMemo(() => {
    const groups = {
      generalInformation: [],
      documentsInformation: [],
      resultClassX: [],
      resultClassXII: [],
      staffDetails: [],
      schoolInfrastructure: [],
      otherInformation: [],
    };

    records.forEach((item) => {
      const group =
        normalizeCategory(item.category);

      groups[group].push(item);
    });

    Object.keys(groups).forEach((key) => {
      groups[key] = sortByPosition(groups[key]);
    });

    return groups;
  }, [records]);

  const lastUpdated = useMemo(() => {
    if (!records.length) return "—";

    const latest = [...records].sort(
      (a, b) =>
        new Date(
          b.updatedAt || b.createdAt || 0
        ).getTime() -
        new Date(
          a.updatedAt || a.createdAt || 0
        ).getTime()
    )[0];

    return formatDate(
      latest.updatedAt || latest.createdAt
    );
  }, [records]);

  const documentCount = useMemo(() => {
    return records.filter((item) =>
      Boolean(getDocumentUrl(item))
    ).length;
  }, [records]);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-12 text-slate-800">

      {/* Background Decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-80 h-80 w-80 rounded-full bg-red-100/50 blur-3xl" />

        <div className="absolute -right-32 top-[800px] h-80 w-80 rounded-full bg-amber-100/50 blur-3xl" />
      </div>

      {/* PAGE HEADER */}
      <section className="relative overflow-hidden bg-gradient-to-r from-red-900 via-red-800 to-red-700 px-4 pb-20 pt-11 text-white sm:px-6 lg:px-8">

        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full border-[35px] border-white/5" />

        <div className="relative mx-auto flex max-w-7xl flex-col justify-between gap-5 sm:flex-row sm:items-center">

          <div>
            <h1 className="font-serif text-[29px] font-medium leading-tight sm:text-[38px] lg:text-[44px]">
              Mandatory Public Disclosure
            </h1>

            <div className="mt-3 h-1 w-20 rounded-full bg-amber-400" />

            <p className="mt-4 max-w-2xl text-sm font-normal leading-6 text-white/85">
              Important school information, official
              documents, academic results, staff details
              and infrastructure records.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3">
            <CalendarDays
              size={20}
              className="text-amber-300"
            />

            <div>
              <p className="text-xs font-normal text-white/70">
                Last Updated
              </p>

              <p className="mt-0.5 text-sm font-medium">
                {lastUpdated}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PAGE CONTENT */}
      <div className="relative z-10 mx-auto -mt-11 max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* INFORMATION SUMMARY */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">

          <div className="flex items-center gap-3 rounded-xl border border-red-100 bg-white px-4 py-3 shadow-sm">
            <FileText
              size={20}
              className="text-red-800"
            />

            <div>
              <p className="text-lg font-medium text-slate-900">
                {records.length}
              </p>

              <p className="text-xs font-normal text-slate-500">
                Public Records
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-red-100 bg-white px-4 py-3 shadow-sm">
            <Download
              size={20}
              className="text-amber-600"
            />

            <div>
              <p className="text-lg font-medium text-slate-900">
                {documentCount}
              </p>

              <p className="text-xs font-normal text-slate-500">
                Linked Documents
              </p>
            </div>
          </div>

          <div className="col-span-2 flex items-center gap-3 rounded-xl border border-red-100 bg-white px-4 py-3 shadow-sm sm:col-span-1">
            <School
              size={20}
              className="text-red-800"
            />

            <p className="text-sm font-normal text-slate-700">
              Public School Information
            </p>
          </div>
        </div>

        {/* API STATUS */}
        {loading ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl bg-white shadow-sm">
            <Loader2
              size={34}
              className="animate-spin text-red-800"
            />

            <p className="mt-4 text-sm text-slate-600">
              Loading disclosures...
            </p>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
            <AlertCircle
              size={32}
              className="mx-auto text-red-600"
            />

            <p className="mt-3 text-sm text-red-700">
              {error}
            </p>

            <button
              type="button"
              onClick={() =>
                setRefreshKey((key) => key + 1)
              }
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-800 px-5 py-2.5 text-sm font-normal text-white transition hover:bg-red-900"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : records.length === 0 ? (
          <div className="rounded-2xl border border-red-100 bg-white px-6 py-12 text-center shadow-sm">
            <FileText
              size={38}
              className="mx-auto text-red-200"
            />

            <h2 className="mt-3 text-lg font-medium text-slate-900">
              No Records Available
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              Mandatory disclosure information has
              not been added yet.
            </p>
          </div>
        ) : (
          <div className="space-y-5">

            <GeneralInformationSection
              items={
                groupedRecords.generalInformation
              }
            />

            <DocumentSection
              title="Documents and Information"
              icon={FileText}
              items={
                groupedRecords.documentsInformation
              }
            />

            <ResultSection
              title="Result Class: X"
              items={groupedRecords.resultClassX}
            />

            <ResultSection
              title="Result Class: XII"
              items={groupedRecords.resultClassXII}
            />

            <DocumentSection
              title="Staff Details"
              icon={Users}
              items={groupedRecords.staffDetails}
            />

            <DocumentSection
              title="School Infrastructure"
              icon={Building2}
              items={
                groupedRecords.schoolInfrastructure
              }
            />

            <DocumentSection
              title="Other Information"
              icon={Info}
              items={groupedRecords.otherInformation}
            />
          </div>
        )}

        {/* BOTTOM INFORMATION */}
        <div className="mt-7 rounded-xl border-l-4 border-amber-400 bg-white px-5 py-4 shadow-sm">
          <p className="text-xs font-normal leading-6 text-slate-600">
            Information displayed on this page is
            maintained through the school administration
            portal. For further clarification, please
            contact the school office.
          </p>
        </div>

      </div>
    </main>
  );
}
